<script setup>
import { onMounted, ref, watch } from "vue";
import dayjs from "dayjs";
import { useListStoreNew } from "@/stores/ListStoreNew";
import PrevNextButtons from "@/components/PrevNextButtons.vue";
import ListSearch from "@/components/ListSearch.vue";
import { useRoute, useRouter, onBeforeRouteUpdate } from "vue-router";
import { usePagination } from "@/composables/usePagination";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";

const list = useListStoreNew();
const route = useRoute();
const router = useRouter();
const filter = ref("all");
const isReady = ref(false);
const sortBy = ref("updated_at");
const sortDirection = ref("DESC");
const sortOptions = [
  { value: "updated_at", label: "Updated" },
  { value: "created_at", label: "Created" },
  { value: "id", label: "ID" },
];
const { setUrl, changePage } = usePagination(list, filter, () => ({
  sort: sortBy.value,
  direction: sortDirection.value,
}));
const { setBreadcrumb } = useBreadcrumbs();

const applyFilter = () => {
  list.clearFilters();
  if (filter.value && filter.value !== "all") {
    list.addFilter("status", filter.value);
  }
};

const applySort = () => {
  if (!list.listService) return;
  list.listService.column = sortBy.value;
  list.listService.direction = sortDirection.value;
};

const load = () => {
  if (!list.listService) return;
  applyFilter();
  applySort();
  setUrl();
  list.resetPage();
};

const setFilter = (val) => {
  filter.value = val;
  load();
};

const setSort = (column) => {
  if (sortBy.value === column) {
    sortDirection.value = sortDirection.value === "ASC" ? "DESC" : "ASC";
  } else {
    sortBy.value = column;
    sortDirection.value = "DESC";
  }
};

const statuses = ["all", "draft", "sent", "accepted", "declined"];

const statusClass = (status) =>
  ({
    sent: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    accepted: "bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
    declined: "bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300",
  })[status] ||
  "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";

const latestVersion = (quote) =>
  (quote.versions || []).reduce(
    (latest, version) =>
      !latest || Number(version.id) > Number(latest.id) ? version : latest,
    null,
  );
const transportRisk = (quote) => latestVersion(quote)?.pts_risk;
const location = (risk, prefix) =>
  [risk?.[`${prefix}_location`], risk?.[`${prefix}_address`]]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ") || "To be confirmed";
const formatDate = (value) =>
  value && dayjs(value).isValid()
    ? dayjs(value).format("D MMM YYYY, HH:mm")
    : "Not recorded";
const sourceLabel = (quote) => {
  if (quote.external_source) {
    return quote.external_source.toLowerCase() === "website"
      ? "Website"
      : `External · ${quote.external_source}`;
  }
  if (quote.created_by?.name) return `Internal · ${quote.created_by.name}`;
  return "Source not recorded";
};

const statusLabel = (status) => {
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : "Draft";
};

const quoteRoute = (quote) => {
  const path = `/quotes/${quote.id}`;
  const ticketId = Number(quote?.ticket_id);
  if (!Number.isNaN(ticketId) && ticketId > 0) {
    return { path, query: { ticket_id: ticketId } };
  }

  return { path };
};

const quoteName = (quote) =>
  quote?.reference?.name ||
  quote?.display_name ||
  quote?.versions?.[0]?.event_risk?.event_name ||
  quote?.versions?.[0]?.pts_risk?.patient_name ||
  quote?.description ||
  "-";

const quoteCode = (quote) =>
  quote?.reference?.txt || `Quote #${quote?.id ?? ""}`;

onMounted(() => {
  list.configure({ remote: "/api/quote", local: "/quotes" });
  filter.value = route.query?.status ?? "all";
  sortBy.value = route.query?.sort ?? "updated_at";
  sortDirection.value = route.query?.direction ?? "DESC";
  applyFilter();
  applySort();
  list.fetchIndex();
  isReady.value = true;
  setBreadcrumb("Quotes");
});

onBeforeRouteUpdate((to, from, next) => {
  if (to.query.page === undefined) {
    load();
  }
  next();
});

watch([sortBy, sortDirection], () => {
  if (!list.listService || !isReady.value) {
    return;
  }
  load();
});
</script>

<template>
  <div class="quote-list space-y-4">
    <div class="box !mb-0">
      <div
        class="grid min-w-0 grid-cols-2 items-center gap-3 sm:flex sm:flex-wrap"
      >
        <ListSearch
          class="col-span-2 min-w-0 sm:flex-1 sm:min-w-[14rem]"
          url="/api/quote/search?query="
          placeholder="Search quotes…"
          :output="(val) => quoteName(val)"
          @selected="(val) => router.push(quoteRoute(val))"
        />
        <div class="flex min-w-0 items-center gap-2">
          <label for="quote-sort" class="sr-only">Sort quotes by</label>
          <select
            id="quote-sort"
            v-model="sortBy"
            class="input min-w-0 flex-1 text-sm rounded-md"
          >
            <option
              v-for="option in sortOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
          <button
            class="btn h-10 px-3 rounded-md"
            :aria-label="
              sortDirection === 'ASC' ? 'Sort descending' : 'Sort ascending'
            "
            @click.prevent="setSort(sortBy)"
          >
            {{ sortDirection === "ASC" ? "↑" : "↓" }}
          </button>
        </div>
        <RouterLink
          class="btn h-10 px-3 rounded-md text-center whitespace-nowrap"
          to="/quotes/create"
          >Create Quote</RouterLink
        >
      </div>
      <div
        class="mt-4 flex flex-wrap gap-2"
        aria-label="Filter quotes by status"
      >
        <button
          v-for="status in statuses"
          :key="status"
          class="min-h-[2.75rem] rounded-full px-4 py-2 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500"
          :class="
            filter === status
              ? 'bg-sky-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
          "
          :aria-pressed="filter === status"
          @click.prevent="setFilter(status)"
        >
          {{ statusLabel(status) }}
        </button>
      </div>
    </div>

    <div
      v-if="list.isLoading"
      class="space-y-3"
      role="status"
      aria-label="Loading quotes"
    >
      <div
        v-for="n in 6"
        :key="n"
        class="rounded-xl border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-900 animate-pulse"
      >
        <div class="h-5 w-2/3 rounded bg-slate-200 dark:bg-slate-700"></div>
        <div
          class="mt-3 h-4 w-1/3 rounded bg-slate-100 dark:bg-slate-800"
        ></div>
        <div class="mt-5 h-12 rounded bg-slate-100 dark:bg-slate-800"></div>
      </div>
      <span class="sr-only">Loading quotes…</span>
    </div>
    <template v-else>
      <div
        v-if="!list.getIndex.length"
        class="box py-10 text-center text-slate-500"
        role="status"
      >
        No quotes found.
      </div>
      <ul v-else class="space-y-3" aria-label="Quotes">
        <li v-for="quote in list.getIndex" :key="quote.id">
          <RouterLink
            :to="quoteRoute(quote)"
            class="quote-card block rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-sky-400 hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 dark:border-slate-700 dark:bg-slate-900 sm:p-5"
          >
            <div
              class="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,0.8fr)] xl:gap-6"
            >
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2 text-xs">
                  <span
                    class="font-medium text-slate-500 dark:text-slate-400"
                    >{{ quoteCode(quote) }}</span
                  >
                  <span
                    class="rounded-full px-2.5 py-1 font-medium"
                    :class="statusClass(quote.status)"
                    >{{ statusLabel(quote.status) }}</span
                  >
                  <span class="text-slate-500 dark:text-slate-400">{{
                    quote.reference_type?.name || "Unknown type"
                  }}</span>
                </div>
                <h2
                  class="mt-2 text-base font-semibold leading-snug text-slate-900 dark:text-slate-100"
                >
                  {{ quoteName(quote) }}
                </h2>
                <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {{
                    quote.customer?.company ||
                    quote.recipient_contact?.name ||
                    "Customer not linked"
                  }}
                </p>
              </div>

              <dl
                v-if="transportRisk(quote)"
                class="grid min-w-0 gap-3 rounded-lg bg-slate-50 p-3 text-sm dark:bg-slate-800/60 sm:grid-cols-2"
              >
                <div class="min-w-0">
                  <dt
                    class="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400"
                  >
                    <span
                      class="h-2 w-2 shrink-0 rounded-full bg-sky-500"
                      aria-hidden="true"
                    ></span
                    >Pick up
                  </dt>
                  <dd
                    class="mt-1 leading-relaxed text-slate-800 dark:text-slate-200"
                  >
                    {{ location(transportRisk(quote), "pickup") }}
                  </dd>
                </div>
                <div class="min-w-0">
                  <dt
                    class="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400"
                  >
                    <span
                      class="h-2 w-2 shrink-0 rounded-full border-2 border-teal-500"
                      aria-hidden="true"
                    ></span
                    >Drop off
                  </dt>
                  <dd
                    class="mt-1 leading-relaxed text-slate-800 dark:text-slate-200"
                  >
                    {{ location(transportRisk(quote), "dropoff") }}
                  </dd>
                </div>
                <div
                  v-if="transportRisk(quote).pickup_time"
                  class="text-xs text-slate-500 dark:text-slate-400 sm:col-span-2"
                >
                  Pickup · {{ formatDate(transportRisk(quote).pickup_time) }}
                </div>
              </dl>
              <div
                v-else
                class="min-w-0 self-center text-sm text-slate-500 dark:text-slate-400"
              >
                <template v-if="latestVersion(quote)?.event_risk?.address"
                  ><span class="mb-1 block text-xs font-medium"
                    >Event location</span
                  >{{ latestVersion(quote).event_risk.address }}</template
                >
                <template v-else>Location to be confirmed</template>
              </div>

              <div
                class="min-w-0 border-t border-slate-100 pt-3 text-xs dark:border-slate-700 xl:border-l xl:border-t-0 xl:pl-5 xl:pt-0"
              >
                <span
                  class="inline-flex max-w-full rounded-md px-2 py-1 font-medium"
                  :class="
                    quote.external_source
                      ? 'bg-violet-50 text-violet-700 dark:bg-violet-950 dark:text-violet-300'
                      : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                  "
                  >{{ sourceLabel(quote) }}</span
                >
                <dl class="mt-3 space-y-1.5 text-slate-500 dark:text-slate-400">
                  <div class="flex flex-wrap gap-x-2">
                    <dt class="font-medium">Created</dt>
                    <dd>
                      <time :datetime="quote.created_at">{{
                        formatDate(quote.created_at)
                      }}</time>
                    </dd>
                  </div>
                  <div class="flex flex-wrap gap-x-2">
                    <dt>Updated</dt>
                    <dd>
                      <time :datetime="quote.updated_at">{{
                        formatDate(quote.updated_at)
                      }}</time>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </RouterLink>
        </li>
      </ul>
      <div class="py-2">
        <PrevNextButtons
          :showPrev="list.showPrev"
          :showNext="list.showNext"
          @prev="changePage(-1)"
          @next="changePage(1)"
        />
      </div>
    </template>
  </div>
</template>

<style scoped>
.quote-card {
  overflow-wrap: anywhere;
}
.quote-list :deep(input) {
  width: 100%;
  min-width: 0;
}
</style>
